import React, { useState, useEffect } from 'react';
import { fundAPI } from '../../services/api';
import { Plus, Trash2, X, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const FundCustomFieldsTab = ({ fundId, fund, onUpdate }) => {
  const { user } = useAuth();
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [showFieldManager, setShowFieldManager] = useState(false);
  const [newCustomField, setNewCustomField] = useState({
    name: '',
    type: 'text',
    options: []
  });
  const [formData, setFormData] = useState({
    custom_fields: fund?.custom_fields || {}
  });
  const [isEditing, setIsEditing] = useState(false);

  // Permission check removed - using parent permissions

  useEffect(() => {
    loadCustomFieldDefinitions();
  }, [fundId]);

  useEffect(() => {
    setFormData({ custom_fields: fund?.custom_fields || {} });
  }, [fund]);

  const loadCustomFieldDefinitions = () => {
    const saved = localStorage.getItem(`custom_fields_fund_${fundId}`);
    if (saved) {
      setCustomFieldDefinitions(JSON.parse(saved));
    }
  };

  const saveCustomFieldDefinitions = (definitions) => {
    localStorage.setItem(`custom_fields_fund_${fundId}`, JSON.stringify(definitions));
    setCustomFieldDefinitions(definitions);
  };

  const addCustomField = () => {
    if (!newCustomField.name.trim()) {
      alert('Field name is required');
      return;
    }

    const field = {
      id: Date.now().toString(),
      name: newCustomField.name.trim(),
      type: newCustomField.type,
      options: newCustomField.options
    };

    const updated = [...customFieldDefinitions, field];
    saveCustomFieldDefinitions(updated);
    setNewCustomField({ name: '', type: 'text', options: [] });
  };

  const removeCustomField = (fieldId) => {
    if (!window.confirm('Remove this custom field? Data will be lost.')) return;
    const updated = customFieldDefinitions.filter(f => f.id !== fieldId);
    saveCustomFieldDefinitions(updated);
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setFormData({
      custom_fields: {
        ...formData.custom_fields,
        [fieldName]: value
      }
    });
  };

  const handleSave = async () => {
    try {
      await fundAPI.update(fundId, { custom_fields: formData.custom_fields });
      setIsEditing(false);
      onUpdate();
      alert('Custom fields saved successfully');
    } catch (error) {
      console.error('Failed to save custom fields:', error);
      alert('Failed to save custom fields');
    }
  };

  const renderCustomField = (field) => {
    const value = formData.custom_fields[field.name] || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            className="w-full px-4 py-2 border rounded-lg disabled:bg-gray-50"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            className="w-full px-4 py-2 border rounded-lg disabled:bg-gray-50"
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            className="w-full px-4 py-2 border rounded-lg disabled:bg-gray-50"
          />
        );
      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            className="w-full px-4 py-2 border rounded-lg disabled:bg-gray-50"
          >
            <option value="">Select...</option>
            {field.options.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options.map((opt, idx) => (
              <label key={idx} className="flex items-center">
                <input
                  type="radio"
                  name={field.name}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                  disabled={!isEditing}
                  className="mr-2"
                />
                {opt}
              </label>
            ))}
          </div>
        );
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            disabled={!isEditing}
            className="w-full px-4 py-2 border rounded-lg disabled:bg-gray-50"
            rows="4"
          />
        );
      case 'checkbox':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.checked)}
              disabled={!isEditing}
              className="mr-2 h-4 w-4"
            />
            <span className="text-sm text-gray-600">Check to enable</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Custom Fields</h3>
        <div className="flex space-x-2">
          {true && (
            <>
              <button
                onClick={() => setShowFieldManager(!showFieldManager)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                title="Manage Custom Fields"
              >
                <Settings size={18} className="mr-2" />
                Manage Fields
              </button>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({ custom_fields: fund?.custom_fields || {} });
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Custom Fields Manager */}
      {showFieldManager && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">Manage Custom Field Definitions</h4>
            <button onClick={() => setShowFieldManager(false)} className="text-gray-500">
              <X size={20} />
            </button>
          </div>

          <div className="mb-4 p-4 bg-white rounded border">
            <h5 className="font-medium mb-3">Add New Custom Field</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="Field Name"
                value={newCustomField.name}
                onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
                className="px-3 py-2 border rounded"
              />
              <select
                value={newCustomField.type}
                onChange={(e) => setNewCustomField({ ...newCustomField, type: e.target.value, options: [] })}
                className="px-3 py-2 border rounded"
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="dropdown">Dropdown</option>
                <option value="radio">Radio Button</option>
                <option value="textarea">Multi-line Text Box</option>
                <option value="checkbox">Checkbox</option>
              </select>
              <button
                onClick={addCustomField}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Field
              </button>
            </div>

            {(newCustomField.type === 'dropdown' || newCustomField.type === 'radio') && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-2">Options (comma-separated):</label>
                <input
                  type="text"
                  placeholder="Option1, Option2, Option3"
                  onChange={(e) => setNewCustomField({
                    ...newCustomField,
                    options: e.target.value.split(',').map(o => o.trim()).filter(o => o)
                  })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            )}
          </div>

          <div>
            <h5 className="font-medium mb-2">Existing Custom Fields:</h5>
            {customFieldDefinitions.length === 0 ? (
              <p className="text-gray-500 text-sm">No custom fields defined yet</p>
            ) : (
              <div className="space-y-2">
                {customFieldDefinitions.map((field) => (
                  <div key={field.id} className="flex items-center justify-between p-3 bg-white border rounded">
                    <div>
                      <span className="font-medium">{field.name}</span>
                      <span className="ml-2 text-sm text-gray-600">({field.type})</span>
                      {field.options.length > 0 && (
                        <span className="ml-2 text-sm text-gray-500">
                          [{field.options.join(', ')}]
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => removeCustomField(field.id)}
                      className="text-red-600 hover:bg-red-50 p-1 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Fields Display/Edit */}
      <div className="bg-white p-6 rounded-lg border">
        {customFieldDefinitions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No custom fields defined.</p>
            {true && (
              <p className="text-sm text-gray-400 mt-2">
                Click "Manage Fields" to add custom fields.
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {customFieldDefinitions.map((field) => (
              <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {field.name}
                </label>
                {renderCustomField(field)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FundCustomFieldsTab;
